import React from "react";
import { useTranslation } from "react-i18next";
import guideImage from "../../../assets/images/guide.png";
import {
  useBillingInfo,
  useIsProCloudPlan,
  useUserHasHelpCenterEnabled,
  useUserStore,
} from "../../../store/userStore";
import { usePenpal } from "../../../hooks/usePenpal";
import { HelpCircle } from "lucide-react";
import UpsellModal from "@/components/modals/UpsellModal";
import { useUIStore } from "@/store/uiStore";
import { Button } from "@/components/ui";

const BillingInfoCard: React.FC<{ cardType?: "vertical" | "horizontal"; hideHelpCenters?: boolean }> = ({
  cardType = "vertical",
  hideHelpCenters = false,
}) => {
  const { t } = useTranslation();
  const { account } = useUserStore();
  const { openExternalUrl } = usePenpal();
  const isProCloudPlan = useIsProCloudPlan();
  const { openModal, modals, closeModal } = useUIStore();
  const handleUpgradeClick = () => {
    const url =
      "https://folge.me/go/cloud?cloudemail=" +
      encodeURIComponent(account?.email || "") +
      "#pricing";
    openExternalUrl(url);
  };

  const billingInfo = useBillingInfo();

  const isHelpCenterEnabled = useUserHasHelpCenterEnabled();  
  const guidesLimitProgress = () => {
    if (!billingInfo) return 0;
    const { guideCreated, guidesLimit } = billingInfo;
    return Math.min(100, Math.floor((guideCreated / guidesLimit) * 100));
  };
  
  const helpCentersLimitProgress = () => {
    if (!billingInfo) return 0;
    const { helpCenterCreated = 0, helpCentersLimit = 1 } = billingInfo;
    return Math.min(100, Math.floor((helpCenterCreated / helpCentersLimit) * 100));
  };

  // const helpCenterItemsLimitProgress = () => {
  //   if (!billingInfo) return 0;
  //   const { helpCenterItemsCreated = 0, helpCenterItemsLimit = 50 } = billingInfo;
  //   return Math.min(100, Math.floor((helpCenterItemsCreated / helpCenterItemsLimit) * 100));
  // };

  const handleHelpClick = () => {
    openModal("upsell");
    // openExternalUrl('https://folge.me/go/cloud?cloudemail=' + encodeURIComponent(account?.email || '') + '#pricing');
  };
  if (isProCloudPlan) {
    return null;
  }

  if (cardType === "horizontal") {
    return (
      <>
        {/* Upsell Modal */}
        <UpsellModal
          isOpen={modals.upsell}
          onClose={() => closeModal("upsell")}
        />
        <div className="flex flex-col gap-3 w-full max-w-[500px]">
          {/* Guides Section */}
          <div className="flex bg-slate-200 rounded-lg shadow-sm px-4 py-3 items-center gap-4">
            {/* Left section - Icon and title */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <img src={guideImage} className="w-5 h-5" alt={t("billing.guideAlt")} />
              <div className="font-bold text-sm text-slate-800">
                {t("billing.onlineGuides")}
              </div>
              <HelpCircle
                className="w-4 h-4 text-slate-500 ml-2 cursor-pointer"
                onClick={handleHelpClick}
              />
            </div>

            {/* Center section - Progress bar and stats */}
            <div className="flex-1 flex flex-col gap-1 items-center">
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-600">
                  {billingInfo?.guideCreated} / {billingInfo?.guidesLimit}
                </span>
              </div>
              <div className="w-full bg-gray-300 rounded-full h-2">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${guidesLimitProgress()}%` }}
                ></div>
              </div>
            </div>

            {/* Right section - Upgrade button */}
            <Button
            size="xs"
            className="flex-shrink-0 rounded-md bg-gradient-to-r from-indigo-600 to-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:from-indigo-700 hover:to-blue-700 shadow-sm"
            onClick={handleUpgradeClick}
          >
            {t("billing.upgradePlan")}
          </Button>
          </div>


          {/* Help Center Items Section */}
        {/* <div className="flex bg-slate-200 rounded-lg shadow-sm px-4 py-3 items-center gap-4">
            <div className="flex items-center gap-2 flex-shrink-0">
              <HelpCircle className="w-5 h-5 text-slate-800" />
              <div className="font-bold text-sm text-slate-800">
                Help Center Items
              </div>
            </div>

            <div className="flex-1 flex flex-col gap-1 items-center">
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-600">
                  {billingInfo?.helpCenterItemsCreated ?? 0} / {billingInfo?.helpCenterItemsLimit ?? 50}
                </span>
              </div>
              <div className="w-full bg-gray-300 rounded-full h-2">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${helpCenterItemsLimitProgress()}%` }}
                ></div>
              </div>
            </div>

            <button
              className="flex-shrink-0 rounded-md bg-indigo-600 disabled:opacity-50 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              onClick={handleUpgradeClick}
            >
              Upgrade plan
            </button>
          </div> */}
        </div>
      </>
    );
  }

  return (
    <>
      {/* Upsell Modal */}
      <UpsellModal
        isOpen={modals.upsell}
        onClose={() => closeModal("upsell")}
      />
      <div className="flex flex-col gap-3 w-full max-w-[250px]">
        {/* Guides Card */}
        <div className="flex flex-col bg-slate-200 rounded-lg shadow-sm px-3 py-3">
          <div className="flex flex-row justify-between items-start mb-2">
            <div className="flex flex-row items-start">
              {/* <img src={guideImage} className="w-4 h-4" alt="Guide" /> */}
              <div className="font-bold text-xs ml-2  text-slate-800">
                {t("billing.onlineGuides")}
              </div>
              <HelpCircle className="w-4 h-4 ml-2 cursor-pointer" onClick={handleHelpClick} />
            </div>
            <div className="text-xs text-slate-600">
              {billingInfo?.guideCreated} / {billingInfo?.guidesLimit}
            </div>
          </div>
          <div className="w-full bg-gray-300 rounded-full h-2 mb-3">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${guidesLimitProgress()}%` }}
            ></div>
          </div>
          <Button
            size="xs"
            className="flex-shrink-0 rounded-md bg-gradient-to-r from-indigo-600 to-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:from-indigo-700 hover:to-blue-700 shadow-sm"
            onClick={handleUpgradeClick}
          >
            {t("billing.upgradePlan")}
          </Button>
        </div>

        {/* Help Centers Card */}
        {isHelpCenterEnabled && !hideHelpCenters && <div className="flex flex-col bg-slate-200 rounded-lg shadow-sm px-3 py-3">
          <div className="flex flex-row justify-between items-start mb-2">
            <div className="flex flex-row items-start">
              <div className="font-bold text-xs ml-2 text-slate-800">
                {t("billing.helpCenters")}
              </div>
            </div>
            <div className="text-xs text-slate-600">
              {billingInfo?.helpCenterCreated ?? 0} / {billingInfo?.helpCentersLimit ?? 1}
            </div>
          </div>
          <div className="w-full bg-gray-300 rounded-full h-2 mb-3">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${helpCentersLimitProgress()}%` }}
            ></div>
          </div>
          <Button
            size="xs"
            className="flex-shrink-0 rounded-md bg-gradient-to-r from-indigo-600 to-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:from-indigo-700 hover:to-blue-700 shadow-sm"
            onClick={handleUpgradeClick}
          >
            {t("billing.upgradePlan")}
          </Button>
        </div>}

        {/* Help Center Items Card */}
        {/* <div className="flex flex-col bg-slate-200 rounded-lg shadow-sm px-3 py-3">
          <div className="flex flex-row justify-between items-start mb-2">
            <div className="flex flex-row items-start">
              <div className="font-bold text-xs ml-2 text-slate-800">
                Help Center Items
              </div>
            </div>
            <div className="text-xs text-slate-600">
              {billingInfo?.helpCenterItemsCreated ?? 0} / {billingInfo?.helpCenterItemsLimit ?? 50}
            </div>
          </div>
          <div className="w-full bg-gray-300 rounded-full h-2 mb-3">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${helpCenterItemsLimitProgress()}%` }}
            ></div>
          </div>
          <button
            className="inline-block rounded-md bg-indigo-600 disabled:opacity-50 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            onClick={handleUpgradeClick}
          >
            Upgrade plan
          </button>
        </div> */}
      </div>
    </>
  );
};

export default BillingInfoCard;
